import { findQuotationLineItem } from "./quotationLineHelpers";

export const DEFAULT_NON_L1_JUSTIFICATION_OPTIONS = [
  "Single Quotation",
  "Overall L1",
  "Client Approval",
  "Management Approval",
  "Delivery / Completion Timeline",
  "Payment Terms",
  "Credit Capacity",
  "Material/ Workmanship Quality",
  "After-Sales Service",
  "Technical Compliance",
  "Warranty / Guarantee Terms",
];

export const NON_L1_JUSTIFICATION_NOTES = {
  "Single Quotation": "Only one vendor quote was received.",
  "Overall L1": "Vendor is L1 overall, but 1-2 line items are non-L1.",
  "Client Approval": "Client approved this vendor selection.",
  "Management Approval": "Approved Internally",
  "Delivery / Completion Timeline": "Faster delivery or completion than other vendors.",
  "Payment Terms": "Better credit period or payment conditions offered.",
  "Credit Capacity": "Can supply on credit or handle larger order value.",
  "Material/ Workmanship Quality": "Better quality material or workmanship offered.",
  "Material / Workmanship Quality": "Better quality material or workmanship offered.",
  "After-Sales Service": "Better post-delivery support or service.",
  "Technical Compliance": "Meets technical specs other vendors didn't.",
  "Warranty / Guarantee Terms": "Better warranty or guarantee coverage offered.",
};

export const getNonL1JustificationNote = (option = "") =>
  NON_L1_JUSTIFICATION_NOTES[String(option || "").trim()] || "";

const RATE_TOLERANCE = 0.01;

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const stripHtml = (value, fallback = "Selected item") => {
  const label = String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  return label || fallback;
};

const latestQuotationSet = (supplier = {}) => supplier?.vendorQuotations?.quotationSets?.slice(-1)?.[0] || {};

const getSupplierName = (supplier = {}) =>
  supplier?.businessName || supplier?.supplierName || supplier?.name || `Vendor ${supplier?.supplierId || ""}`.trim();

const normalizeSupplierRank = (rank) => {
  const value = String(rank || "").trim();
  if (!value) return "";
  return /^l\d+$/i.test(value) ? value.toUpperCase() : value;
};

const getSupplierRank = (supplier = {}) =>
  normalizeSupplierRank(supplier?.vendorQuotations?.rank || supplier?.quotationDetails?.rank || supplier?.rank);

export const getNonL1JustificationOptions = (userEntity = {}) => {
  const options = userEntity?.nonL1JustificationOptions;
  if (!Array.isArray(options) || options.length === 0) return DEFAULT_NON_L1_JUSTIFICATION_OPTIONS;
  return [...new Set(options.map((option) => String(option || "").trim()).filter(Boolean))];
};

const getValidQuotationSuppliers = (suppliers = []) =>
  (Array.isArray(suppliers) ? suppliers : []).filter(
    (supplier) => supplier?.quotationReceived && supplier?.vendorQuotations?.quotationSets?.length
  );

const getSelectedEntries = (selectedMaterialsMap = {}) =>
  Object.entries(selectedMaterialsMap || {}).flatMap(([supplierId, entries = []]) =>
    (entries || []).map((entry) => ({ ...entry, supplierId }))
  );

const findSupplier = (suppliers = [], supplierId) =>
  suppliers.find((supplier) => String(supplier?.supplierId) === String(supplierId));

const getDiscountedMaterialRate = (item = {}) => {
  const basePrice = toNumber(item?.basePrice ?? item?.selectedItemRate ?? item?.rate ?? item?.price);
  const discount = toNumber(item?.discount);
  return basePrice - (basePrice * discount) / 100;
};

const getMaterialLowestRate = ({ suppliers, rfqMaterial, rowIndex }) => {
  let lowest = Infinity;

  suppliers.forEach((supplier) => {
    const material = findQuotationLineItem(latestQuotationSet(supplier)?.materialDetails, rfqMaterial, rowIndex);
    const rate = material ? getDiscountedMaterialRate(material) : Infinity;
    if (rate > 0 && rate < lowest) lowest = rate;
  });

  return lowest === Infinity ? null : lowest;
};

const getLabourLowestRate = ({ suppliers, rfqLabour, rowIndex }) => {
  let lowest = Infinity;

  suppliers.forEach((supplier) => {
    const labour = findQuotationLineItem(latestQuotationSet(supplier)?.labourItems, rfqLabour, rowIndex, {
      rfqTextFields: ["skill"],
      quoteTextFields: ["skill", "name"],
    });
    const rate = toNumber(labour?.labourRate ?? labour?.selectedLabourRate, Infinity);
    if (rate > 0 && rate < lowest) lowest = rate;
  });

  return lowest === Infinity ? null : lowest;
};

const getContractorSections = (rfqData = {}) => rfqData?.providingFixingDetails || [];

const findContractorSection = (supplier, sectionId) =>
  (latestQuotationSet(supplier)?.contractorDetails || []).find((section) => String(section?.id) === String(sectionId));

const findContractorScope = (supplier, sectionId, scopeId) =>
  findContractorSection(supplier, sectionId)?.scopes?.find((scope) => String(scope?.id) === String(scopeId));

const getContractorScopeUnitRate = (scope = {}) => {
  const quantity = toNumber(scope?.quantity, 0);
  if (quantity <= 0) return toNumber(scope?.totalExclGst ?? scope?.rate ?? scope?.price, 0);
  return toNumber(scope?.totalExclGst, 0) / quantity;
};

const getContractorLowestComparableRate = ({ suppliers, sectionId, scopeId, isMaterialBacked }) => {
  let lowest = Infinity;

  suppliers.forEach((supplier) => {
    const scope = findContractorScope(supplier, sectionId, scopeId);
    const rate = isMaterialBacked ? toNumber(scope?.totalExclGst, Infinity) : getContractorScopeUnitRate(scope);
    if (rate > 0 && rate < lowest) lowest = rate;
  });

  return lowest === Infinity ? null : lowest;
};

const addUniqueSupplier = (map, supplier = {}) => {
  const key = String(supplier?.supplierId || supplier?.businessName || "");
  if (!key) return;
  map.set(key, {
    supplierId: supplier?.supplierId,
    businessName: getSupplierName(supplier),
    rank: getSupplierRank(supplier),
  });
};

const buildSingleQuotationContext = ({ selectedEntries, suppliers, rfqType }) => {
  const selectedSuppliers = new Map();
  selectedEntries.forEach((entry) => addUniqueSupplier(selectedSuppliers, findSupplier(suppliers, entry.supplierId)));

  return {
    requiresJustification: selectedEntries.length > 0,
    triggerType: "SINGLE_QUOTATION",
    rfqType,
    title: "Single quotation selected",
    summary: "Only one valid quotation is available for this RFQ. Please record why this award is acceptable.",
    selectedSuppliers: Array.from(selectedSuppliers.values()),
    lines: [],
  };
};

const evaluateMaterialSelection = ({ rfqData, suppliers, selectedEntries }) => {
  const nonL1Lines = [];
  const selectedSuppliers = new Map();

  selectedEntries.forEach((entry) => {
    const rowIndex = (rfqData?.materials || []).findIndex((material) => String(material?._id) === String(entry?.materialId));
    const rfqMaterial = rowIndex >= 0 ? rfqData.materials[rowIndex] : null;
    if (!rfqMaterial) return;

    const lowestRate = getMaterialLowestRate({ suppliers, rfqMaterial, rowIndex });
    const selectedRate = toNumber(entry?.selectedItemRate, null);

    if (lowestRate !== null && selectedRate !== null && selectedRate > lowestRate + RATE_TOLERANCE) {
      const supplier = findSupplier(suppliers, entry.supplierId);
      addUniqueSupplier(selectedSuppliers, supplier);
      nonL1Lines.push({
        itemName: stripHtml(rfqMaterial?.name, "Selected material"),
        supplierId: supplier?.supplierId,
        supplierName: getSupplierName(supplier),
        supplierRank: getSupplierRank(supplier),
        selectedRate,
        lowestRate,
      });
    }
  });

  return { nonL1Lines, selectedSuppliers };
};

const evaluateLabourSelection = ({ rfqData, suppliers, selectedEntries }) => {
  const nonL1Lines = [];
  const selectedSuppliers = new Map();

  selectedEntries.forEach((entry) => {
    const rowIndex = (rfqData?.labourDetails || []).findIndex((labour) => String(labour?._id) === String(entry?.labourId));
    const rfqLabour = rowIndex >= 0 ? rfqData.labourDetails[rowIndex] : null;
    if (!rfqLabour) return;

    const lowestRate = getLabourLowestRate({ suppliers, rfqLabour, rowIndex });
    const selectedRate = toNumber(entry?.selectedLabourRate, null);

    if (lowestRate !== null && selectedRate !== null && selectedRate > lowestRate + RATE_TOLERANCE) {
      const supplier = findSupplier(suppliers, entry.supplierId);
      addUniqueSupplier(selectedSuppliers, supplier);
      nonL1Lines.push({
        itemName: stripHtml(rfqLabour?.skill, "Selected labour"),
        supplierId: supplier?.supplierId,
        supplierName: getSupplierName(supplier),
        supplierRank: getSupplierRank(supplier),
        selectedRate,
        lowestRate,
      });
    }
  });

  return { nonL1Lines, selectedSuppliers };
};

const evaluateContractorSelection = ({ rfqData, suppliers, selectedEntries }) => {
  const nonL1Lines = [];
  const selectedSuppliers = new Map();
  const sections = getContractorSections(rfqData);

  selectedEntries.forEach((entry) => {
    const section = sections.find((item) => String(item?._id) === String(entry?.sectionId));
    const scope = section?.scopes?.find((item) => String(item?._id) === String(entry?.scopeId));
    if (!section || !scope) return;

    const isMaterialBacked = Array.isArray(scope?.materials) && scope.materials.length > 0;
    const lowestRate = getContractorLowestComparableRate({
      suppliers,
      sectionId: section?._id,
      scopeId: scope?._id,
      isMaterialBacked,
    });
    const selectedRate = isMaterialBacked
      ? toNumber(entry?.selectedSupplierPrice, null)
      : toNumber(entry?.selectedScopeRate, null);

    if (lowestRate !== null && selectedRate !== null && selectedRate > lowestRate + RATE_TOLERANCE) {
      const supplier = findSupplier(suppliers, entry.supplierId);
      addUniqueSupplier(selectedSuppliers, supplier);
      nonL1Lines.push({
        itemName: `${stripHtml(section?.sectionName, "Section")} / ${stripHtml(scope?.scopeOfWork, "Selected scope")}`,
        supplierId: supplier?.supplierId,
        supplierName: getSupplierName(supplier),
        supplierRank: getSupplierRank(supplier),
        selectedRate,
        lowestRate,
      });
    }
  });

  return { nonL1Lines, selectedSuppliers };
};

export const buildAwardJustificationContext = ({
  rfqData = {},
  rfqType = "",
  suppliersWithQuotation = [],
  selectedMaterialsMap = {},
}) => {
  const suppliers = getValidQuotationSuppliers(suppliersWithQuotation);
  const selectedEntries = getSelectedEntries(selectedMaterialsMap);

  if (suppliers.length <= 1) {
    return buildSingleQuotationContext({
      selectedEntries,
      suppliers: suppliers.length ? suppliers : suppliersWithQuotation,
      rfqType,
    });
  }

  let result = { nonL1Lines: [], selectedSuppliers: new Map() };
  if (rfqType === "material") {
    result = evaluateMaterialSelection({ rfqData, suppliers, selectedEntries });
  } else if (rfqType === "labour") {
    result = evaluateLabourSelection({ rfqData, suppliers, selectedEntries });
  } else if (rfqType === "contractor") {
    result = evaluateContractorSelection({ rfqData, suppliers, selectedEntries });
  }

  const lines = result.nonL1Lines || [];
  return {
    requiresJustification: lines.length > 0,
    triggerType: "NON_L1_SELECTION",
    rfqType,
    title: "Non-L1 selection detected",
    summary: "One or more selected lines are above the current lowest valid quote. Please select the most appropriate justification from the options below",
    selectedSuppliers: Array.from(result.selectedSuppliers?.values?.() || []),
    lines,
  };
};

export const formatJustificationLineRate = (value) =>
  Number.isFinite(Number(value))
    ? new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(Number(value))
    : "-";

export const hasAwardJustificationHistory = (rfqData = {}) =>
  Array.isArray(rfqData?.awardJustifications) && rfqData.awardJustifications.length > 0;
